import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Play, Pause, Radio as RadioIcon, Share2, Copy, Check, Search,
  Users, Signal, Music, Send, Loader2, X as XIcon,
} from "lucide-react";
import api from "../services/api";
import { usePlayer } from "../context/PlayerContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const DEFAULT_ART = "/microphone.png";
const NP_POLL_MS = 15000;

// Public, shareable, no-login radio page served at /r/:slug.
export default function PublicRadio() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const player = usePlayer();

  const [radio, setRadio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [np, setNp] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const [requestable, setRequestable] = useState([]);
  const [reqLoaded, setReqLoaded] = useState(false);
  const [reqAvailable, setReqAvailable] = useState(true);
  const [query, setQuery] = useState("");
  const [requestingId, setRequestingId] = useState(null);

  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null); // { type: "success"|"error", msg }
  const toastTimer = useRef(null);

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // --- Load the radio -------------------------------------------------------
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    setRadio(null);
    api.get(`/api/radios/public/${encodeURIComponent(slug)}`)
      .then((res) => { if (mounted) setRadio(res.data); })
      .catch((err) => {
        if (!mounted) return;
        if (err?.response?.status === 404) setNotFound(true);
        else setNotFound(true);
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  // --- Now playing polling --------------------------------------------------
  useEffect(() => {
    if (!radio?.id) return undefined;
    let mounted = true;
    const load = () => api.get(`/api/radios/${radio.id}/nowplaying`)
      .then((r) => {
        if (!mounted) return;
        setNp(r.data);
        setElapsed(Number(r.data?.now_playing?.elapsed) || 0);
      })
      .catch(() => { if (mounted) setNp(null); });
    load();
    const poll = setInterval(load, NP_POLL_MS);
    return () => { mounted = false; clearInterval(poll); };
  }, [radio?.id]);

  // Smooth local ticker for the progress bar between polls.
  useEffect(() => {
    const duration = Number(np?.now_playing?.duration) || 0;
    if (!duration) return undefined;
    const tick = setInterval(() => {
      setElapsed((e) => (e < duration ? e + 1 : e));
    }, 1000);
    return () => clearInterval(tick);
  }, [np?.now_playing?.duration, np?.now_playing?.song?.id]);

  // --- Requestable songs ----------------------------------------------------
  useEffect(() => {
    if (!radio?.id) return undefined;
    let mounted = true;
    api.get(`/api/radios/${radio.id}/requestable`)
      .then((r) => {
        if (!mounted) return;
        setRequestable(Array.isArray(r.data) ? r.data : []);
        setReqAvailable(true);
      })
      .catch(() => { if (mounted) setReqAvailable(false); })
      .finally(() => { if (mounted) setReqLoaded(true); });
    return () => { mounted = false; };
  }, [radio?.id]);

  // --- Derived --------------------------------------------------------------
  const streamUrl = radio?.listen_url || radio?.stream_url || "";
  const stationUuid = radio ? `radio-${radio.id}` : null;
  const isCurrent = !!(radio && player?.current?.station_uuid === stationUuid);
  const isPlaying = isCurrent && player?.playing;

  const isOnline = !!np?.is_online;
  const isLive = !!np?.live?.is_live;
  const listeners = np?.listeners?.current ?? 0;
  const song = np?.now_playing?.song || {};
  const npText = song.text || [song.artist, song.title].filter(Boolean).join(" — ");
  const duration = Number(np?.now_playing?.duration) || 0;
  const progressPct = duration ? Math.min(100, (elapsed / duration) * 100) : 0;

  const shareUrl = useMemo(
    () => (typeof window !== "undefined" ? `${window.location.origin}/r/${slug}` : `/r/${slug}`),
    [slug]
  );
  const shareText = radio ? `${radio.name} — ${t("publicRadio.shareText")}` : "Waveo";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return requestable;
    return requestable.filter((item) => {
      const s = item.song || item;
      return `${s.title || ""} ${s.artist || ""} ${s.text || ""}`.toLowerCase().includes(q);
    });
  }, [requestable, query]);

  // --- Actions --------------------------------------------------------------
  const togglePlay = () => {
    if (!radio || !streamUrl) return;
    player?.play({
      station_uuid: stationUuid,
      name: radio.name,
      url: streamUrl,
      favicon: radio.logo || DEFAULT_ART,
      country: radio.genre,
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      showToast("error", t("publicRadio.copyFailed"));
    }
  };

  const requestSong = async (item) => {
    const reqId = item.request_id ?? item.requestId ?? item.id;
    if (reqId == null || !radio) return;
    setRequestingId(reqId);
    try {
      await api.post(`/api/radios/${radio.id}/request/${encodeURIComponent(reqId)}`);
      showToast("success", t("publicRadio.requestSuccess"));
    } catch (err) {
      const msg = err?.response?.data?.message || t("publicRadio.requestError");
      showToast("error", msg);
    } finally {
      setRequestingId(null);
    }
  };

  // --- Render ---------------------------------------------------------------
  if (loading) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-32 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mb-3 text-purple-500" />
          <p>{t("common.loading")}</p>
        </div>
      </Shell>
    );
  }

  if (notFound || !radio) {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-28 text-center px-4">
          <div className="w-16 h-16 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center mb-5">
            <RadioIcon className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("publicRadio.notFoundTitle")}</h1>
          <p className="text-gray-500 max-w-md mb-6">{t("publicRadio.notFoundDesc")}</p>
          <Link to="/radios" className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl font-medium">
            {t("publicRadio.browseRadios")}
          </Link>
        </div>
      </Shell>
    );
  }

  const art = radio.logo || DEFAULT_ART;

  return (
    <Shell>
      {/* Toast */}
      {toast && (
        <div className="fixed top-20 inset-x-0 z-50 flex justify-center px-4 pointer-events-none">
          <div className={`pointer-events-auto flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium shadow-lg ${toast.type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
            {toast.type === "success" ? <Check className="w-4 h-4" /> : <XIcon className="w-4 h-4" />}
            <span>{toast.msg}</span>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 pt-24 pb-20">
        {/* Hero card */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white shadow-xl">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,white,transparent_60%)]" />
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <img
              src={art}
              alt=""
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover bg-white/10 shadow-lg shrink-0"
              onError={(e) => { e.currentTarget.src = DEFAULT_ART; }}
            />
            <div className="flex-1 min-w-0 text-center sm:text-start">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${isOnline ? "bg-white/20" : "bg-black/20 text-white/70"}`}>
                  <Signal className="w-3.5 h-3.5" />
                  {isOnline ? (isLive ? t("publicRadio.live") : t("publicRadio.online")) : t("publicRadio.offline")}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20">
                  <Users className="w-3.5 h-3.5" />
                  {t("publicRadio.listeners", { count: listeners })}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight truncate">{radio.name}</h1>
              <div className="mt-2 text-white/80 text-sm flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                {[radio.genre, radio.language].filter(Boolean).map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-white/10">{tag}</span>
                ))}
                {radio.owner_firstname && (
                  <span className="text-white/70">{t("publicRadio.by", { name: radio.owner_firstname })}</span>
                )}
              </div>
              {radio.description && (
                <p className="mt-3 text-white/85 text-sm max-w-xl leading-relaxed">{radio.description}</p>
              )}

              <div className="mt-5 flex items-center justify-center sm:justify-start gap-3">
                <button
                  onClick={togglePlay}
                  disabled={!streamUrl}
                  className="inline-flex items-center gap-2 bg-white text-purple-700 hover:bg-purple-50 disabled:opacity-50 font-bold px-6 py-3 rounded-full shadow-md transition"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  {isPlaying ? t("publicRadio.pause") : t("publicRadio.playLive")}
                </button>
              </div>
            </div>
          </div>

          {/* Now playing strip */}
          <div className="relative border-t border-white/15 bg-black/10 px-6 sm:px-8 py-4">
            <div className="flex items-center gap-3">
              {song.art ? (
                <img src={song.art} alt="" className="w-12 h-12 rounded-lg object-cover bg-white/10 shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Music className="w-5 h-5" /></div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-wide text-white/60">{t("publicRadio.nowPlaying")}</p>
                <p className="font-semibold truncate">
                  {npText || (isOnline ? t("publicRadio.onAir") : t("publicRadio.offlineMsg"))}
                </p>
                {duration > 0 && (
                  <div className="mt-2 h-1 rounded-full bg-white/20 overflow-hidden">
                    <div className="h-full bg-white/80 transition-all duration-1000 ease-linear" style={{ width: `${progressPct}%` }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Share */}
        <section className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Share2 className="w-4 h-4 text-purple-600" /> {t("publicRadio.shareTitle")}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-[200px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
            />
            <button onClick={copyLink} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? t("publicRadio.copied") : t("publicRadio.copyLink")}
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <ShareIntent label="X" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`} className="bg-black hover:bg-gray-800" />
            <ShareIntent label="Facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} className="bg-[#1877F2] hover:bg-[#0f66d0]" />
            <ShareIntent label="WhatsApp" href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`} className="bg-[#25D366] hover:bg-[#1eb455]" />
          </div>
        </section>

        {/* Song requests */}
        <section className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Music className="w-4 h-4 text-purple-600" /> {t("publicRadio.requestTitle")}
          </h2>
          <p className="text-sm text-gray-500 mb-4">{t("publicRadio.requestSubtitle")}</p>

          {!reqLoaded ? (
            <p className="text-gray-400 text-sm">{t("common.loading")}</p>
          ) : !reqAvailable || requestable.length === 0 ? (
            <p className="text-gray-400 text-sm">{t("publicRadio.requestsUnavailable")}</p>
          ) : (
            <>
              <div className="relative mb-4">
                <Search className="w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 start-3" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("publicRadio.searchPlaceholder")}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 ps-9 pe-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                />
              </div>

              {filtered.length === 0 ? (
                <p className="text-gray-400 text-sm">{t("publicRadio.noResults")}</p>
              ) : (
                <ul className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {filtered.slice(0, 60).map((item, i) => {
                    const s = item.song || item;
                    const reqId = item.request_id ?? item.requestId ?? item.id;
                    const busy = requestingId === reqId;
                    return (
                      <li key={reqId ?? i} className="flex items-center gap-3 py-3">
                        {s.art ? (
                          <img src={s.art} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center shrink-0"><Music className="w-4 h-4" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{s.title || s.text || t("publicRadio.unknownTitle")}</p>
                          <p className="text-xs text-gray-500 truncate">{s.artist || "—"}</p>
                        </div>
                        <button
                          onClick={() => requestSong(item)}
                          disabled={busy || reqId == null}
                          className="inline-flex items-center gap-1.5 text-sm bg-purple-50 hover:bg-purple-100 text-purple-700 disabled:opacity-50 px-3 py-1.5 rounded-lg font-medium whitespace-nowrap"
                        >
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          {t("publicRadio.request")}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </section>
      </main>

      {/* Branding footer */}
      <footer className="border-t border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-start">
          <p className="text-gray-500 text-sm">
            {t("publicRadio.poweredBy")} <Link to="/" className="font-bold text-purple-600">Waveo</Link>
          </p>
          <Link to="/signup" className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 text-white px-5 py-2.5 rounded-full font-medium text-sm">
            <RadioIcon className="w-4 h-4" /> {t("publicRadio.createCta")}
          </Link>
        </div>
      </footer>
    </Shell>
  );
}

// Minimal public shell: sticky top bar with logo + language switcher.
function Shell({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#f7f9fc] flex flex-col">
      <header className="fixed top-0 inset-x-0 z-40 bg-white/90 backdrop-blur shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="text-xl font-extrabold text-purple-600 tracking-tight">Waveo</Link>
          <div className="flex items-center gap-3">
            <Link to="/radios" className="hidden sm:inline text-sm font-medium text-gray-600 hover:text-purple-600">
              {t("nav.radios")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function ShareIntent({ label, href, className }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={`inline-flex items-center justify-center text-white text-xs font-semibold px-3 py-2 rounded-lg ${className}`}
    >
      {label}
    </a>
  );
}
